import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Button,
  Divider,
  Tooltip,
} from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupIcon from "@mui/icons-material/Group";
import EventIcon from "@mui/icons-material/Event";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TitleHeader from "../../../components/CustomComponents/title";
import { useTranslation } from "react-i18next";
import landingApi from "../../../contexts/landingApiClient";
import EarningStatCard from "../../../components/affiliate/EarningStatCard";
import InfoCard from "../../../components/affiliate/InfoCard";
import CommissionListTable from "../../../components/affiliate/CommissionListTable";
import PayoutsListTable from "../../../components/affiliate/PayoutsListTable";
import AffiliateEventsTable from "../../../components/affiliate/AffiliateEventsTable";
import CommonButton from "../../../components/CustomComponents/Buttons/CommonButton";

const AffiliateDetails = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [payoutToEdit, setPayoutToEdit] = useState(null);
  const [currentMonthEarning, setCurrentMonthEarning] = useState(null);

  const fetchAffiliateDetails = async () => {
    try {
      const response = await landingApi.get(`/affiliates/${id}`, {
        params: {
          populate: ["commissionDetails", "payoutDetails", "tier"],
        },
      });
      setAffiliate(response.data.data);
    } catch (err) {
      setError(t("error_fetch_affiliate_details"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentMonthEarning = async () => {
    try {
      const response = await landingApi.get(
        `/commission-detail/update-current-month/${id}`
      );
      if (response.data && response.data.status) {
        setCurrentMonthEarning(response.data.data.currentMonthEarning);
      }
    } catch (err) {
      console.error("Error fetching current month earning:", err);
      // Fallback to commissionDetails value if API fails
    }
  };

  useEffect(() => {
    fetchAffiliateDetails();
    fetchCurrentMonthEarning();
  }, [id]);

  const handlePayoutCreated = () => {
    fetchAffiliateDetails();
    fetchCurrentMonthEarning();
  };

  const handlePayoutDeleted = () => {
    fetchAffiliateDetails();
    fetchCurrentMonthEarning();
  };

  const handleOpenModal = () => {
    setPayoutToEdit(null);
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setPayoutToEdit(null);
  };

  const handleEditPayout = (payout) => {
    setPayoutToEdit(payout);
    setModalOpen(true);
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!affiliate) {
    return <Typography>{t("no_affiliate_data_found")}</Typography>;
  }

  const { attributes } = affiliate;
  const commissionDetails = attributes.commissionDetails;
  const payoutDetails = attributes.payoutDetails;
  const totalCommission = commissionDetails?.totalCommission || 0;
  const totalPaid = commissionDetails?.totalPaid || 0;
  const availableBalance = totalCommission - totalPaid;
  const isPayoutDisabled = availableBalance <= 0;

  const affiliateInfoDetails = [
    { label: t("affiliate_id"), value: affiliate.id },
    { label: t("name"), value: attributes.name },
    { label: t("email"), value: attributes.email },
    { label: t("referral_code"), value: attributes.referralCode },
    { label: t("status"), value: t(attributes.status) },
    {
      label: t("member_since"),
      value: new Date(attributes.createdAt).toLocaleDateString(),
    },
  ];

  const payoutInfoDetails = payoutDetails
    ? [
        { label: t("method"), value: payoutDetails.method },
        { label: t("account_email"), value: payoutDetails.accountEmail || "-" },
        { label: t("iban_number"), value: payoutDetails.ibanNumber },
        { label: t("bank_name"), value: payoutDetails.bankName },
      ]
    : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 140px)",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "#F6F6F7",
          py: 2,
          px: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <CommonButton
          color="inherit"
          onSubmit={() => navigate("/admin/affiliate")}
          label={t("back")}
          displayBlack={"true"}
        />
        <Tooltip
          title={
            isPayoutDisabled
              ? t("no_balance_available_for_payout")
              : ""
          }
          arrow
        >
          <span>
            <CommonButton
              color="inherit"
              onSubmit={handleOpenModal}
              label={t("create_payout")}
              disabled={isPayoutDisabled}
            />
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ overflowY: "auto", flexGrow: 1, pt: 6 }}>
        <TitleHeader title={attributes.name || t("affiliate_details")} />
        <Grid container spacing={3}>
          {commissionDetails ? (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <EarningStatCard
                  icon={<MonetizationOnIcon />}
                  value={`${"€"}${totalCommission.toFixed(2)}`}
                  label={t("total_commission")}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EarningStatCard
                  icon={<PaymentIcon />}
                  value={`${"€"}${totalPaid.toFixed(2)}`}
                  label={t("total_paid")}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EarningStatCard
                  icon={<AccountBalanceWalletIcon />}
                  value={`${"€"}${(totalCommission - totalPaid).toFixed(2)}`}
                  label={t("current_balance")}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EarningStatCard
                  icon={<HourglassEmptyIcon />}
                  value={`${"€"}${
                    commissionDetails.pendingPayouts?.toFixed(2) || "0.00"
                  }`}
                  label={t("pending_payouts")}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EarningStatCard
                  icon={<TrendingUpIcon />}
                  value={`${"€"}${
                    currentMonthEarning !== null
                      ? currentMonthEarning.toFixed(2)
                      : commissionDetails.currentMonthEarning?.toFixed(2) ||
                        "0.00"
                  }`}
                  label={t("current_month_earning")}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EarningStatCard
                  icon={<GroupIcon />}
                  value={commissionDetails.totalReferrals || "0"}
                  label={t("total_referrals")}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EarningStatCard
                  icon={<EventIcon />}
                  value={
                    commissionDetails.nextPayoutDate
                      ? new Date(
                          commissionDetails.nextPayoutDate
                        ).toLocaleDateString()
                      : "-"
                  }
                  label={t("next_payout_date")}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EarningStatCard
                  icon={<LocationOnIcon />}
                  value={attributes.locationsCount || "0"}
                  label={t("total_locations")}
                  loading={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <EarningStatCard
                  icon={<EmojiEventsIcon />}
                  value={
                    attributes.tier?.data?.attributes?.level
                      ? `${t("tier_level")} ${
                          attributes.tier.data.attributes.level
                        }`
                      : t("no_tier")
                  }
                  label={t("current_tier")}
                  loading={loading}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Typography>{t("no_commission_details_available")}</Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <InfoCard
              title={t("affiliate_information")}
              details={affiliateInfoDetails}
            />
          </Grid>

          <Grid item xs={12}>
            <InfoCard title={t("payout_details")} details={payoutInfoDetails} />
          </Grid>

          <Grid item xs={12}>
            <AffiliateEventsTable affiliateId={id} />
          </Grid>

          <Grid item xs={12}>
            <CommissionListTable affiliateId={id} />
          </Grid>

          <Grid item xs={12}>
            <PayoutsListTable
              affiliateId={id}
              modalOpen={modalOpen}
              handleCloseModal={handleCloseModal}
              onPayoutCreated={handlePayoutCreated}
              onPayoutDeleted={handlePayoutDeleted}
              onEditPayout={handleEditPayout}
              payoutToEdit={payoutToEdit}
              availableBalance={commissionDetails?.pendingPayouts || 0}
              affiliate={affiliate}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AffiliateDetails;
